import React, { useId, useState } from 'react';
import { Clock3, Megaphone } from 'lucide-react';
import { GoogleAdsSignal } from '../../../types/results.types';

interface GoogleAdsSignalBadgeProps {
  signal?: GoogleAdsSignal | null;
}

const ageInDays = (value?: string | null): number | null => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return null;
  return Math.max(0, Math.floor((Date.now() - timestamp) / 86_400_000));
};

const GoogleAdsSignalBadge: React.FC<GoogleAdsSignalBadgeProps> = ({ signal }) => {
  const [open, setOpen] = useState(false);
  const tooltipId = useId();

  if (!signal || signal.status !== 'detected') return null;

  const age = ageInDays(signal.last_detected_at);
  const stale = signal.freshness === 'stale';
  const checkedLabel = signal.last_detected_at
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(signal.last_detected_at))
    : 'date unavailable';

  return (
    <span
      className="relative mt-1 inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setOpen(value => !value);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        aria-expanded={open}
        aria-describedby={open ? tooltipId : undefined}
        aria-label={stale && age != null
          ? `Google Ads tracking found, checked ${age} days ago and may be out of date`
          : 'Google Ads tracking found recently'}
        className="inline-flex items-center gap-1 rounded-[5px] border border-[#C9DEF5] bg-[#EAF2FB] px-1.5 py-0.5 text-[10px] font-semibold text-[#1C5DA5] transition focus:outline-none focus:ring-2 focus:ring-[#2B6CB0]/40"
      >
        <Megaphone className="h-2.5 w-2.5" aria-hidden="true" />
        Google Ads
        {stale && age != null && (
          <span className="ml-0.5 inline-flex items-center gap-0.5 border-l border-[#C9DEF5] pl-1 text-[#B45309]">
            <Clock3 className="h-2.5 w-2.5" aria-hidden="true" />
            {age}d
          </span>
        )}
      </button>

      {open && (
        <span
          id={tooltipId}
          role="tooltip"
          className="absolute left-0 top-full z-[80] mt-2 w-64 rounded-lg bg-[#2D3142] p-3 text-left text-[11px] font-normal leading-4 text-gray-200 shadow-xl"
        >
          <span className="block font-semibold text-white">Google Ads tracking found</span>
          <span className="mt-1 block">
            A Google Ads conversion or remarketing signal was found on this website. It may reflect an active, paused, historical, or remarketing setup.
          </span>
          <span className="mt-2 block text-gray-400">Checked {checkedLabel}.</span>
        </span>
      )}
    </span>
  );
};

export default GoogleAdsSignalBadge;
